/**
 * @fileoverview Screenshot Upload Component for Instagram lead capture
 * Provides drag-and-drop interface for uploading Instagram screenshots
 * and displays extracted contact information
 */

import React, { useState, useCallback, useRef } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import './ScreenshotUpload.css';

/**
 * Screenshot Upload Component
 * 
 * Features:
 * - Drag and drop file upload
 * - File selection via click
 * - Image preview
 * - Upload progress tracking
 * - Extracted data display
 * - Error handling
 * 
 * @component
 * @returns {React.ReactElement} The rendered ScreenshotUpload component
 */
const ScreenshotUpload = () => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Allowed file types
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const maxFiles = 2;
  const maxSize = 10 * 1024 * 1024; // 10MB

  /**
   * Validate uploaded files
   * @param {FileList} fileList - Files to validate
   * @returns {Array} Array of valid files
   */
  const validateFiles = useCallback((fileList) => {
    const validFiles = [];
    const errors = [];

    if (fileList.length > maxFiles) {
      errors.push(`Maximum ${maxFiles} files allowed`);
      return { validFiles: [], errors };
    }

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      
      // Check file type - handle HEIC files that might not have proper MIME type
      const fileName = file.name.toLowerCase();
      const isValidType = allowedTypes.includes(file.type) || 
                         fileName.endsWith('.heic') || 
                         fileName.endsWith('.heif');
      
      if (!isValidType) {
        errors.push(`${file.name}: Invalid file type. Please use JPG, PNG, WebP, or HEIC`);
        continue;
      }
      
      // Check file size
      if (file.size > maxSize) {
        errors.push(`${file.name}: File too large. Maximum size is 10MB`);
        continue;
      }
      
      validFiles.push(file);
    }

    return { validFiles, errors };
  }, []);

  /**
   * Create image previews for uploaded files
   * @param {Array} fileArray - Array of files
   */
  const createPreviews = useCallback((fileArray) => {
    const previewPromises = fileArray.map(file => {
      return new Promise((resolve) => {
        const fileName = file.name.toLowerCase();
        const isHeic = fileName.endsWith('.heic') || fileName.endsWith('.heif');
        
        if (isHeic) {
          // HEIC files can't be previewed in browser, show placeholder
          resolve({
            file,
            url: null, // No preview URL
            name: file.name,
            size: file.size,
            isHeic: true
          });
        } else {
          const reader = new FileReader();
          reader.onload = (e) => resolve({
            file,
            url: e.target.result,
            name: file.name,
            size: file.size,
            isHeic: false
          });
          reader.onerror = () => resolve({
            file,
            url: null,
            name: file.name,
            size: file.size,
            isHeic: false
          });
          reader.readAsDataURL(file);
        }
      });
    });

    Promise.all(previewPromises).then(setPreviews);
  }, []);

  /**
   * Handle file selection (drag & drop or click)
   * @param {FileList} fileList - Selected files
   */
  const handleFileSelection = useCallback((fileList) => {
    setError(null);
    setResults(null);
    
    const { validFiles, errors } = validateFiles(fileList);
    
    if (errors.length > 0) {
      setError(errors.join('. '));
      return;
    }
    
    if (validFiles.length === 0) {
      setError('No valid files selected');
      return;
    }

    setFiles(validFiles);
    createPreviews(validFiles);
  }, [validateFiles, createPreviews]);

  /**
   * Handle drag events
   */
  const handleDrag = useCallback((e) => {
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  /**
   * Handle drop event
   */
  const handleDrop = useCallback((e) => {
    setDragActive(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      handleFileSelection(droppedFiles);
    }
  }, [handleFileSelection]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback((e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFileSelection(selectedFiles);
    }
  }, [handleFileSelection]);

  /**
   * Open file dialog
   */
  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Remove a file from selection
   * @param {number} index - Index of file to remove
   */
  const removeFile = useCallback((index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    setFiles(newFiles);
    setPreviews(newPreviews);
    
    if (newFiles.length === 0) {
      setResults(null);
      setError(null);
    }
  }, [files, previews]);

  /**
   * Upload files and process screenshots
   */
  const uploadFiles = useCallback(async () => {
    if (files.length === 0) {
      setError('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(API_ENDPOINTS.SCREENSHOTS.UPLOAD, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      setResults(result);
      
      // Clear files after successful upload
      setTimeout(() => {
        setFiles([]);
        setPreviews([]);
        setUploadProgress(0);
      }, 2000);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  }, [files]);

  /**
   * Clear all files and reset state
   */
  const clearAll = useCallback(() => {
    setFiles([]);
    setPreviews([]);
    setResults(null);
    setError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="screenshot-upload">
      <div className="upload-header">
        <h1>Instagram Screenshot Upload</h1>
        <p>Upload 1-2 Instagram screenshots to extract contact information</p>
      </div>

      {/* Drop Zone */}
      <div
        ref={dropZoneRef}
        className={`drop-zone ${dragActive ? 'drag-active' : ''} ${files.length > 0 ? 'has-files' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
        
        <div className="drop-zone-content">
          <div className="upload-icon">📱</div>
          <h3>Drop Instagram screenshots here</h3>
          <p>or <span className="click-text">click to select files</span></p>
          <div className="file-requirements">
            <p>• Maximum 2 files</p>
            <p>• JPG, PNG, WebP, or HEIC format</p>
            <p>• Maximum 10MB per file</p>
            <p>• HEIC files automatically converted to JPG</p>
          </div>
        </div>
      </div>

      {/* File Previews */}
      {previews.length > 0 && (
        <div className="file-previews">
          <h3>Selected Files ({previews.length}/{maxFiles})</h3>
          <div className="preview-grid">
            {previews.map((preview, index) => (
              <div key={index} className="preview-item">
                <div className="preview-image">
                  {preview.isHeic || !preview.url ? (
                    <div className="heic-placeholder">
                      <div className="heic-icon">📱</div>
                      <div className="heic-text">
                        {preview.isHeic ? 'HEIC Image' : 'Image'}
                        <br />
                        <small>Preview not available</small>
                      </div>
                    </div>
                  ) : (
                    <img src={preview.url} alt={`Preview ${index + 1}`} />
                  )}
                  <button
                    className="remove-file"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
                <div className="preview-info">
                  <p className="file-name">{preview.name}</p>
                  <p className="file-size">{formatFileSize(preview.size)}</p>
                  {preview.isHeic && <p className="file-note">Will be converted to JPG</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Controls */}
      {files.length > 0 && (
        <div className="upload-controls">
          <button
            className="upload-btn"
            onClick={uploadFiles}
            disabled={uploading}
          >
            {uploading ? 'Processing...' : 'Upload & Process Screenshots'}
          </button>
          <button
            className="clear-btn"
            onClick={clearAll}
            disabled={uploading}
          >
            Clear All
          </button>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p>Processing screenshots... {uploadProgress}%</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="error-message">
          <h3>Error</h3>
          <p>{error}</p>
        </div>
      )}

      {/* Results Display */}
      {results && (
        <div className="results">
          <h3>Extraction Results</h3>
          <div className="results-grid">
            <div className="result-item">
              <label>First Name:</label>
              <span className={results.extracted_data.first_name ? 'found' : 'not-found'}>
                {results.extracted_data.first_name || 'Not found'}
              </span>
            </div>
            <div className="result-item">
              <label>Last Name:</label>
              <span className={results.extracted_data.last_name ? 'found' : 'not-found'}>
                {results.extracted_data.last_name || 'Not found'}
              </span>
            </div>
            <div className="result-item">
              <label>Instagram Username:</label>
              <span className={results.extracted_data.instagram_username ? 'found' : 'not-found'}>
                {results.extracted_data.instagram_username || 'Not found'}
              </span>
            </div>
            <div className="result-item">
              <label>Phone Number(s):</label>
              <span className={results.extracted_data.phone_number || (results.extracted_data.all_phone_numbers && results.extracted_data.all_phone_numbers.length > 0) ? 'found' : 'not-found'}>
                {results.extracted_data.all_phone_numbers && results.extracted_data.all_phone_numbers.length > 0 
                  ? results.extracted_data.all_phone_numbers.join(', ')
                  : results.extracted_data.phone_number || 'Not found'
                }
              </span>
            </div>
            <div className="result-item">
              <label>Email:</label>
              <span className={results.extracted_data.email ? 'found' : 'not-found'}>
                {results.extracted_data.email || 'Not found'}
              </span>
            </div>
            <div className="result-item">
              <label>Screenshot Type:</label>
              <span>{results.extracted_data.screenshot_type || 'Unknown'}</span>
            </div>
            <div className="result-item">
              <label>Confidence:</label>
              <span className={`confidence-${results.extracted_data.confidence}`}>
                {results.extracted_data.confidence || 'Medium'}
              </span>
            </div>
          </div>
          
          {results.extracted_data.other_info && (
            <div className="other-info">
              <label>Additional Information:</label>
              <p>{results.extracted_data.other_info}</p>
            </div>
          )}
          
          <div className="success-message">
            <p>✅ Data saved to database with ID: {results.document_id}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScreenshotUpload;
